import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import { LeaveGuardConfirmDialog } from 'components/Common/LeaveGuardDialog';
import { exportToCSV } from 'components/Common/CSVManage';
import {
  hasOptedOutOfLeaveWarning,
  optOutOfLeaveWarning,
} from 'hooks/useLeaveGuard';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Composer, { Popover } from './Composer';
import RecentScreens from './RecentScreens';
import ScreeningWait from './ScreeningWait';
import ResultBoxes from './ResultBoxes';
import ResultsTable, { FilterChip, SortKey } from './ResultsTable';
import FiltersModal from './FiltersModal';
import { EvaluationDrawer, MatchDrawer } from './Drawers';
import { DialogLayer } from './ScreeningDialog';
import { useWidening } from './useWidening';
import StaleNotice from './StaleNotice';
import { useProductScreening } from './useProductScreening';
import {
  AsOf,
  MatchRow,
  RecentScreen,
  RunInput,
  Scope,
  ScopeKey,
} from './types';
import {
  Jurisdiction,
  ScreeningProfile,
  jurisdictionOf,
  partnerPhrase,
  routeLabel,
} from './profiles';
import {
  HTS_KEYS,
  PAGE_SIZE,
  countScope,
  emptyScope,
  longDate,
  parseQuery,
  querySubject,
  queryTerm,
  sameAsOf,
  sameQuery,
  titleCase,
} from './utils';
import 'resources/styles/screening-composer.css';

const REQUIRED_TIERS = ['pro', 'business', 'enterprise'];

const RESULTS_CSV_HEADERS = [
  'Product Name',
  'HS Code',
  'Source Country',
  'Agency',
  'List Name',
  'Date Updated',
];

const CHIP_GROUP: Record<ScopeKey, string> = {
  lens: 'Lens:',
  country: 'Listing country:',
  agency: 'Agency:',
  list: 'List:',
  hlist: 'Code list:',
  section: 'Section:',
  chapter: 'Chapter:',
  code: 'HTS:',
};

const scrollToResults = () =>
  document
    .getElementById('psc-results')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

interface NextStepLocation {
  pathname: string;
  search?: string;
  state?: Record<string, unknown>;
}

const asArray = (v: any): string[] => {
  if (Array.isArray(v)) {
    return v.filter((x) => typeof x === 'string' && x.trim() !== '');
  }
  return typeof v === 'string' && v.trim() !== '' ? [v] : [];
};

const ScreeningComposerPage: React.FC<{ profile: ScreeningProfile }> = ({
  profile,
}) => {
  const { cur_user, isWhiteLabel, ticketDraft } = useContext(GlobalContext);
  const location = useLocation<any>();
  const history = useHistory();
  const handoff = (location.state || {}) as {
    primary?: string;
    lenses?: string[] | string;
    lists?: string[] | string;
    country?: string;
    tradeType?: string;
  };

  const hasAccess =
    REQUIRED_TIERS.indexOf(cur_user?.license) !== -1 ||
    Boolean(isWhiteLabel) ||
    cur_user?.bite_super_admin === true;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].indexOf(
      cur_user?.license,
    ) !== -1
      ? cur_user.license
      : 'none'
  ) as LicenseType;
  const isRestricted = cur_user?.role === 'domain-restricted';

  const api = useProductScreening(
    profile,
    hasAccess && Boolean(cur_user?.selected_team),
  );

  const [q, setQ] = useState<string>(handoff.primary || '');
  const [partner, setPartner] = useState<string>(handoff.country || '');
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>(() =>
    jurisdictionOf(profile, handoff.tradeType),
  );
  const [asOf, setAsOf] = useState<AsOf>({ mode: 'today', from: '', to: '' });
  const [archived, setArchived] = useState(false);
  const [scope, setScope] = useState<Scope>(() => ({
    ...emptyScope(),
    lens: asArray(handoff.lenses),
    list: asArray(handoff.lists),
  }));
  const [openPop, setOpenPop] = useState<Popover | null>(null);

  const [tableQuery, setTableQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey | ''>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [drawer, setDrawer] = useState(-1);
  const [evalOpen, setEvalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);

  const parsed = useMemo(() => parseQuery(q), [q]);
  const scopeCount = countScope(scope, jurisdiction.htsFilters);
  const { phase } = api;
  useWidening(composerRef, phase === 'idle' ? 'narrow' : 'wide');

  const input = useCallback(
    (over: Partial<RunInput> = {}): RunInput => ({
      q: q,
      partner: partner,
      jurisdiction: jurisdiction,
      scope: scope,
      asOf: asOf,
      archived: archived,
      ...over,
    }),
    [q, partner, jurisdiction, scope, asOf, archived],
  );

  const resetTableState = () => {
    setTableQuery('');
    setSortKey('');
    setSortDir('asc');
    setPage(0);
    setDrawer(-1);
    setEvalOpen(false);
  };

  const runScreen = (over: Partial<RunInput> = {}) => {
    setOpenPop(null);
    resetTableState();
    api.run(input(over));
  };

  const { loadCatalog } = api;
  useEffect(() => {
    loadCatalog(jurisdiction);
  }, [loadCatalog, jurisdiction]);

  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current || !hasAccess || !handoff.primary) {
      return;
    }
    autoRan.current = true;
    if (parseQuery(handoff.primary).kind !== 'badhts') {
      runScreen();
    }
  }, [hasAccess]);

  const browse =
    parsed.kind === 'empty' && api.lastRun?.parsed.kind === 'empty';
  const shownAll = useMemo(() => {
    const t = tableQuery.trim().toLowerCase();
    let out = api.rows.filter(
      (r) =>
        !t ||
        [r.name, r.hs, r.list, r.agency, r.country].some(
          (v) => v.toLowerCase().indexOf(t) !== -1,
        ),
    );
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      out = out
        .slice()
        .sort(
          (a, b) => String(a[sortKey]).localeCompare(String(b[sortKey])) * dir,
        );
    }
    return out;
  }, [api.rows, tableQuery, sortKey, sortDir]);

  const pageCount = browse
    ? Math.min(200, Math.ceil(Math.min(api.total, 10000) / PAGE_SIZE))
    : Math.ceil(shownAll.length / PAGE_SIZE);
  const currentPage = browse ? api.serverPage : page;
  const pageRows = browse
    ? shownAll
    : shownAll.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const firstIdx = currentPage * PAGE_SIZE;
  const rangeText = browse
    ? api.total
      ? `Showing ${(firstIdx + 1).toLocaleString()} to ${(
          firstIdx + api.rows.length
        ).toLocaleString()} of ${api.total.toLocaleString()} records`
      : 'No records'
    : shownAll.length
    ? `Showing ${(firstIdx + 1).toLocaleString()} to ${Math.min(
        firstIdx + PAGE_SIZE,
        shownAll.length,
      ).toLocaleString()} of ${shownAll.length.toLocaleString()} ${
        shownAll.length === 1 ? 'match' : 'matches'
      }`
    : tableQuery
    ? `0 of ${api.total.toLocaleString()} matches`
    : 'No matches';

  const onPage = (n: number) => {
    if (browse && api.lastRun) {
      api.run({ ...api.lastRun }, n, { pageOnly: true });
    } else {
      setPage(n);
    }
    scrollToResults();
  };

  const applyScope = (next: Scope) => {
    setScope(next);
    setOpenPop(null);
    if (phase === 'done') {
      runScreen({ scope: next });
    }
  };

  const chips: FilterChip[] = [];
  (Object.keys(CHIP_GROUP) as ScopeKey[]).forEach((dim) => {
    if (!jurisdiction.htsFilters && HTS_KEYS.indexOf(dim) !== -1) {
      return;
    }
    scope[dim].forEach((v) => {
      chips.push({
        key: `${dim}:${v}`,
        group: CHIP_GROUP[dim],
        label: v,
        onRemove: () =>
          applyScope({ ...scope, [dim]: scope[dim].filter((x) => x !== v) }),
      });
    });
  });

  const clearFilters = () => applyScope(emptyScope());
  const resetTable = () => {
    resetTableState();
    if (chips.length === 0) {
      return;
    }
    const cleared = emptyScope();
    setScope(cleared);
    setOpenPop(null);
    if (phase === 'done' && api.lastRun) {
      api.run({ ...api.lastRun, scope: cleared });
    }
  };

  const runnable =
    parsed.kind === 'hts' ||
    parsed.kind === 'desc' ||
    (parsed.kind === 'empty' && scopeCount > 0);
  const cannotRunWhy =
    parsed.kind === 'badhts'
      ? 'HTS codes have 6, 8 or 10 digits'
      : 'Enter an HTS code or a product description, or pick filters to browse';

  const subject = querySubject(parsed, 'every record');
  const scopePhrase = scopeCount
    ? `${scopeCount} ${scopeCount === 1 ? 'filter' : 'filters'}`
    : `every ${jurisdiction.noun} list`;
  const runLine = `Screening ${subject}${partnerPhrase(
    profile,
    partner,
  )} against ${scopePhrase}.`;

  const shown = phase === 'done' ? api.lastRun : null;
  const stale: 'query' | 'options' | null = !shown
    ? null
    : !sameQuery(parsed, shown.parsed)
    ? 'query'
    : !sameAsOf(asOf, shown.asOf) ||
      archived !== shown.archived ||
      partner !== shown.partner
    ? 'options'
    : null;
  const changedOptions = shown
    ? [
        partner !== shown.partner ? profile.partner.key : '',
        !sameAsOf(asOf, shown.asOf) ? 'date range' : '',
        archived !== shown.archived ? 'archived setting' : '',
      ].filter(Boolean)
    : [];

  let statusLine = '';
  if (shown && stale === 'query') {
    const back = `Esc returns to ${querySubject(
      shown.parsed,
      'the last screen',
    )}`;
    statusLine = runnable
      ? `Enter runs a new screen · ${back}`
      : `${cannotRunWhy} · ${back}`;
  } else if (shown && stale === 'options') {
    statusLine = `Run again to apply the new ${changedOptions.join(' and ')}`;
  } else if (phase === 'done' && api.lastRun) {
    statusLine = api.savedTicket
      ? `Saved to Saved Work · ${
          api.savedTicket.freq === 'Once'
            ? 'saved once'
            : `re-screens ${api.savedTicket.freq.toLowerCase()}`
        }`
      : `${api.total.toLocaleString()} ${
          api.total === 1 ? 'match' : 'matches'
        } · ${longDate(api.lastRun.at)}`;
  } else if (phase !== 'done' && handoff.primary && q === handoff.primary) {
    const lensNote = scope.lens.length
      ? `, scoped to ${scope.lens.join(', ')}`
      : '';
    statusLine = `Opened with ${
      parsed.kind === 'hts' ? parsed.fmt : q
    }${partnerPhrase(profile, partner)}${lensNote}`;
  } else if (api.screenError) {
    statusLine = api.screenError;
  }

  const defaultTicketName =
    ticketDraft?.ticketName ||
    (api.lastRun
      ? [
          queryTerm(api.lastRun.parsed) || 'Filtered browse',
          api.lastRun.partner,
          api.lastRun.jurisdiction.label,
        ]
          .filter(Boolean)
          .join(' · ')
      : '');

  const nextStepOptOutKey = `screening-next-step-leave-warning-optout-${
    cur_user?.basics?.id || 'anon'
  }`;
  const [nextStepTarget, setNextStepTarget] = useState<NextStepLocation | null>(
    null,
  );
  const [leaveSaved, setLeaveSaved] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [optedOutThisSession, setOptedOutThisSession] = useState(false);

  const nextStepLocation = (): NextStepLocation | null => {
    if (!shown) {
      return null;
    }
    const hts = shown.parsed.kind === 'hts' ? shown.parsed.fmt : '';
    if (profile.direction === 'Export') {
      return {
        pathname: profile.nextStep.to,
        state: { destination: shown.partner, referenceHts: hts },
      };
    }
    const tab = shown.jurisdiction.tariffCountry;
    if (!tab) {
      return null;
    }
    return {
      pathname: profile.nextStep.to,
      search: tab === 'us' ? '' : `?country=${tab}`,
      state: { primary: hts, origin: shown.partner },
    };
  };
  const nextStep = nextStepLocation();

  const onNextStep = (target: NextStepLocation) => {
    if (optedOutThisSession || hasOptedOutOfLeaveWarning(nextStepOptOutKey)) {
      history.push(target);
      return;
    }
    setLeaveSaved(Boolean(api.savedTicket));
    setDontAskAgain(false);
    setNextStepTarget(target);
  };

  const leaveForNextStep = () => {
    if (!nextStepTarget) {
      return;
    }
    if (!leaveSaved && dontAskAgain) {
      optOutOfLeaveWarning(nextStepOptOutKey);
      setOptedOutThisSession(true);
    }
    setNextStepTarget(null);
    history.push(nextStepTarget);
  };

  const saveThenLeave = async () => {
    if (
      await api.saveTicket(defaultTicketName, [], 'Once', { redirect: false })
    ) {
      leaveForNextStep();
    }
  };

  const onJurisdiction = (j: Jurisdiction) => {
    if (j.id === jurisdiction.id) {
      return;
    }
    const cleared = emptyScope();
    setJurisdiction(j);
    setScope(cleared);
    setOpenPop(null);
    if (phase === 'done') {
      runScreen({ jurisdiction: j, scope: cleared });
    }
  };

  const onPartner = (v: string) => {
    setPartner(v);
    if (phase === 'done' && api.lastRun && v !== api.lastRun.partner) {
      setOpenPop(null);
      resetTableState();
      api.run({ ...api.lastRun, partner: v });
    }
  };

  const onRevert = () => {
    if (api.lastRun) {
      setQ(api.lastRun.q);
      setPartner(api.lastRun.partner);
      setAsOf(api.lastRun.asOf);
      setArchived(api.lastRun.archived);
    }
  };

  const onPickRecent = (r: RecentScreen) => {
    const j = jurisdictionOf(profile, r.jurisdiction);
    const cleared = emptyScope();
    setQ(r.q);
    setPartner(r.partner);
    setJurisdiction(j);
    setScope(cleared);
    runScreen({ q: r.q, partner: r.partner, jurisdiction: j, scope: cleared });
  };

  if (!cur_user?.selected_team) {
    return <Redirect to='/' />;
  }

  const stageClass =
    phase === 'idle' ? '' : phase === 'run' ? ' is-run' : ' is-done';
  const { lastRun } = api;
  const evalData = api.evalState.data;
  const evalAnnouncement =
    api.evalState.status === 'done' && evalData
      ? `${profile.direction} risk evaluated: ${
          evalData.risk_level ? titleCase(evalData.risk_level) : 'no rating'
        }. Trade burden: ${
          evalData.burden ? titleCase(evalData.burden) : 'no rating'
        }.`
      : api.evalState.status === 'error'
      ? 'The AI evaluation did not respond. Your matches are unaffected.'
      : '';

  return (
    <DialogLayer.Provider value={sectionRef}>
      <section
        ref={sectionRef}
        className='psc'
        aria-label={`${profile.direction} screening`}
      >
        <div className='psc-page'>
          {!isWhiteLabel && !hasAccess && (
            <RequiredLicense
              userLicense={normalizedLicense}
              minimumLicense='pro'
            />
          )}

          <div className='psc-actions'>
            <button
              type='button'
              className='psc-btn psc-btn--icon psc-btn--ghost'
              aria-label='Help'
              title={profile.copy.help}
            >
              <HelpOutlineIcon sx={{ fontSize: 17 }} />
            </button>
          </div>

          <div className={`psc-stage${stageClass}`}>
            <div className='psc-top'>
              <div className='psc-hero'>
                <h1>{profile.copy.title}</h1>
                <p>{profile.copy.lede}</p>
              </div>
            </div>

            <div ref={composerRef} className='psc-composer'>
              <Composer
                query={{
                  inputRef: inputRef,
                  value: q,
                  onChange: setQ,
                  parsed: parsed,
                  recents: api.recents,
                  onPickRecent: onPickRecent,
                }}
                route={{
                  profile: profile,
                  jurisdiction: jurisdiction,
                  onJurisdiction: onJurisdiction,
                  partner: partner,
                  onPartner: onPartner,
                }}
                options={{
                  asOf: asOf,
                  onAsOf: setAsOf,
                  archived: archived,
                  onToggleArchived: () => setArchived((v) => !v),
                }}
                run={{
                  phase: phase,
                  canRun: hasAccess && runnable,
                  cannotRunWhy: hasAccess
                    ? cannotRunWhy
                    : `${profile.direction} screening needs a Pro plan or higher`,
                  onRun: () => runScreen(),
                  stale: stale,
                  onRevert: onRevert,
                  statusLine: statusLine,
                }}
                ticket={{
                  canSave: !isWhiteLabel,
                  defaultName: defaultTicketName,
                  savedFreq: api.savedTicket ? api.savedTicket.freq : null,
                  saving: api.saving,
                  matchCount: api.total,
                  onSave: api.saveTicket,
                }}
                disabled={!hasAccess}
                openPop={openPop}
                setOpenPop={setOpenPop}
              />
            </div>

            <div className='psc-bottom'>
              {phase === 'idle' && (
                <RecentScreens
                  profile={profile}
                  jurisdiction={jurisdiction}
                  recents={api.recents}
                  recordTotal={api.recordTotal}
                  onPick={onPickRecent}
                  onBrowse={() => setOpenPop('scope')}
                />
              )}

              {phase === 'run' && (
                <ScreeningWait
                  runLine={runLine}
                  secs={api.secs}
                  showEval={!isRestricted}
                  riskLabel={`${profile.direction} risk`}
                />
              )}

              {phase === 'done' && lastRun && (
                <div className='psc-results'>
                  <h2 className='psc-sr'>Screening results</h2>
                  <span className='psc-sr' role='status'>
                    {evalAnnouncement}
                  </span>
                  {stale && (
                    <StaleNotice
                      kind={stale}
                      shownFor={`${querySubject(
                        lastRun.parsed,
                        'these filters',
                      )}${partnerPhrase(profile, lastRun.partner)}`}
                      changedOptions={changedOptions}
                      canRun={hasAccess && runnable}
                      onRun={() => runScreen()}
                      onRevert={onRevert}
                    />
                  )}
                  <ResultBoxes
                    profile={profile}
                    evalState={api.evalState}
                    parsed={lastRun.parsed}
                    jurisdiction={lastRun.jurisdiction}
                    partner={partner}
                    rows={api.rows}
                    total={api.total}
                    partial={browse}
                    showEval={!isRestricted}
                    listFilter={tableQuery}
                    onPickList={(l) => {
                      setTableQuery(l);
                      setPage(0);
                    }}
                    onOpenEval={() => setEvalOpen(true)}
                    onAddPartner={() => setOpenPop('partner')}
                    onFocusQuery={() => inputRef.current?.focus()}
                    onOpenFilters={() => setOpenPop('scope')}
                    onRetry={() => api.retryEvaluation()}
                    onViewResults={scrollToResults}
                    onNextStep={
                      nextStep ? () => onNextStep(nextStep) : undefined
                    }
                  />
                  <ResultsTable
                    rows={pageRows}
                    shownCount={browse ? api.total : shownAll.length}
                    total={api.total}
                    filteredByText={
                      !browse &&
                      Boolean(tableQuery.trim()) &&
                      shownAll.length !== api.rows.length
                    }
                    query={tableQuery}
                    onQuery={(v) => {
                      setTableQuery(v);
                      setPage(0);
                    }}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={(k) => {
                      setSortDir(
                        sortKey === k && sortDir === 'asc' ? 'desc' : 'asc',
                      );
                      setSortKey(k);
                    }}
                    onOpenRow={(r: MatchRow) => setDrawer(shownAll.indexOf(r))}
                    activeRow={drawer >= 0 ? shownAll[drawer] || null : null}
                    chips={chips}
                    onOpenFilters={() => setOpenPop('scope')}
                    onClearFilters={clearFilters}
                    onDownloadCsv={() =>
                      exportToCSV(
                        `${profile.direction.toLowerCase()}-screening-results.csv`,
                        api.rows.map((row) => ({ _source: row.source })),
                        RESULTS_CSV_HEADERS,
                        'commodity',
                      )
                    }
                    onResetTable={resetTable}
                    resetDisabled={
                      !tableQuery &&
                      !sortKey &&
                      currentPage === 0 &&
                      chips.length === 0
                    }
                    page={currentPage}
                    pageCount={pageCount}
                    onPage={onPage}
                    rangeText={rangeText}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {drawer >= 0 && drawer < shownAll.length && (
          <MatchDrawer
            rows={shownAll}
            index={drawer}
            onIndex={setDrawer}
            onClose={() => setDrawer(-1)}
          />
        )}

        {evalOpen && evalData && lastRun && (
          <EvaluationDrawer
            data={evalData}
            sub={`${lastRun.parsed.fmt} · ${routeLabel(
              profile,
              lastRun.jurisdiction,
              partner,
              `No ${profile.partner.key}`,
            )}`}
            listsRead={new Set(api.rows.map((r) => r.list)).size}
            totalMatches={api.total}
            showsTradeCategory={lastRun.jurisdiction.showsTradeCategory}
            onClose={() => setEvalOpen(false)}
          />
        )}

        {openPop === 'scope' && (
          <FiltersModal
            scope={scope}
            jurisdiction={jurisdiction}
            listRows={api.listRows}
            htsRows={api.htsRows}
            codeLists={api.codeLists}
            loading={api.catalogLoading}
            recordTotal={api.recordTotal}
            savedFilters={api.savedFilters}
            onCreateSaved={api.createSavedFilter}
            onUpdateSaved={api.updateSavedFilter}
            onDeleteSaved={api.deleteSavedFilter}
            applyLabel={phase === 'done' ? 'Re-run screen' : 'Apply filters'}
            onApply={applyScope}
            onCancel={() => setOpenPop(null)}
          />
        )}

        <LeaveGuardConfirmDialog
          open={nextStepTarget !== null}
          title={
            leaveSaved
              ? `${profile.nextStep.label}?`
              : 'Save this screen before you leave?'
          }
          body={
            leaveSaved
              ? "You'll leave this screen. It's saved as a ticket in Saved Work, so you can come back to it."
              : `${profile.nextStep.label} leaves this screen, and it isn't saved yet. Save it as a ticket to keep its matches and AI evaluation in Saved Work.`
          }
          onClose={() => setNextStepTarget(null)}
          onLeaveWithoutSaving={leaveForNextStep}
          leaveLabel={leaveSaved ? 'Continue' : 'Leave without saving'}
          save={
            leaveSaved
              ? undefined
              : { label: 'Save ticket first', onClick: saveThenLeave }
          }
          dontAskAgain={
            leaveSaved
              ? undefined
              : { checked: dontAskAgain, onChange: setDontAskAgain }
          }
          busy={api.saving}
        />
      </section>
    </DialogLayer.Provider>
  );
};

export default ScreeningComposerPage;
